import { AccountType } from "@common/constants/domains/accounting/accounts";
import { leafGroupHeader } from "@common/constants/domains/accounting/accountGroups";
import { Account } from "@common/types/domains/accounting/account";

export interface AccountGroup {
    type: string;
    accounts: Account[];
}

/**
 * Groups accounts by leaf account type, in the order they come from the API;
 * `suggestedType` pins the groups made only of that type to the top.
 */
export const groupAccounts = (
    accounts: Account[],
    suggestedType: AccountType | undefined,
): AccountGroup[] => {
    const groupMap = new Map<string, Account[]>();
    for (const account of accounts) {
        const groupKey = leafGroupHeader.keyOf(account);
        const existing = groupMap.get(groupKey);
        if (existing) {
            existing.push(account);
        } else {
            groupMap.set(groupKey, [account]);
        }
    }
    const groups = Array.from(groupMap.entries()).map(([type, accs]) => ({
        type,
        accounts: accs,
    }));
    if (!suggestedType) {
        return groups;
    }
    const isSuggested = (group: AccountGroup) =>
        group.accounts.every((account) => account.type === suggestedType);
    return [
        ...groups.filter(isSuggested),
        ...groups.filter((group) => !isSuggested(group)),
    ];
};

/** Whether an account matches the typed query by name or account code. */
export const matchesAccountSearch = (account: Account, query: string) => {
    const lower = query.toLowerCase();
    return (
        account.name.toLowerCase().includes(lower) ||
        (account.codeText ?? "").toLowerCase().includes(lower)
    );
};
